import { useState, useEffect, useRef, lazy, Suspense, forwardRef, useImperativeHandle } from 'react';
import { pathOr, propOr, equals } from 'ramda';
import { Link } from 'react-router-dom';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { grey } from '@mui/material/colors';

/* Form */
import { Formik, useFormik, useFormikContext } from 'formik';
import PhoneNumberInput from 'components/forms/PhoneNumberInput';
import ZipcodeInput from 'components/inputs/ZipcodeInput';

/* i18n */
import { useIntl } from 'react-intl';

/* API */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from 'lib/API';
import { updateExperience } from 'lib/API/experience';
import { updateUserData } from 'lib/API/user';

/* Styles */
import recStyles from 'styles/recommendations-list.module.scss';
import cssVars from 'styles/vars.module.scss';

/* Analytics */
import TimeMe from 'timeme.js';
import { DISTANCE_OPTIONS } from 'constants/distanceOptions';
import { ARKANSAS_CITIES } from 'constants/arkansasCities';

/* Material UI and other UI Dependencies */
import {
  Container,
  Typography,
  Divider,
  Box,
  Chip,
  TextField,
  Button,
  FormHelperText,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Stack,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Alert,
  AlertTitle,
  CircularProgress,
  InputAdornment,
} from '@mui/material';

/* Redux */
import { useDispatch } from 'react-redux';
import { setOpenContactInfo } from 'app/slices/experienceSlice';
import { setZipcode } from 'app/slices/experienceSlice';
import { setPreferredName } from 'app/slices/appSlice';

/* UI Components */
import { useSnackbar } from 'notistack';
import { LoadingSmall } from 'components/Loading';

import CustomNotification from 'components/CustomNotification';
import LeavingPageDialog from 'components/LeavingPageDialog';
import ConfirmationDialog from 'components/ConfirmationDialog';
import InfoTooltip from 'components/forms/InfoTooltip';
import YesNoRadioGroup from 'components/forms/YesNoRadioGroup';
import { MILITARY_EXPERIENCE } from 'components/profile/constants';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import ToggleSwitch from 'components/ToggleSwitch';
const JSTMain = lazy(() => import('components/JSTcomponents/JSTMain'));
import { DatePicker } from '@mui/x-date-pickers';
import { useUpdateUser } from 'lib/hooks/userQueries';

/* Utils */
import zipcodes from 'zipcodes';
import { preventSubmitOnEnter } from 'lib/utils';

const validateMilitaryDate = (value, intl) => {
  const d = new Date(value);
  if (isNaN(d.getTime())) return intl.formatMessage({ id: 'profile.military.validation.dateFormat' });
  if (d.getFullYear() < 1900) return intl.formatMessage({ id: 'profile.military.validation.invalidDate' });
  if (d > new Date()) return intl.formatMessage({ id: 'profile.military.validation.dateFuture' });
  return null;
};

const DISCHARGE_STATUS_OPTIONS = [
  { value: 'honorable', labelId: 'profile.military.dischargeStatus.honorable' },
  { value: 'general', labelId: 'profile.military.dischargeStatus.general' },
  { value: 'other_than_honorable', labelId: 'profile.military.dischargeStatus.otherThanHonorable' },
  { value: 'bad_conduct_special', labelId: 'profile.military.dischargeStatus.badConductSpecial' },
  { value: 'bad_conduct_general', labelId: 'profile.military.dischargeStatus.badConductGeneral' },
  { value: 'dishonorable', labelId: 'profile.military.dischargeStatus.dishonorable' },
  { value: 'officer', labelId: 'profile.military.dischargeStatus.officer' },
  { value: 'medical', labelId: 'profile.military.dischargeStatus.medical' },
  { value: 'entry_level', labelId: 'profile.military.dischargeStatus.entryLevel' },
];

const formatDateForAPI = (date) => {
  if (!date) return null;
  try {
    return new Date(date).toISOString().slice(0, 10);
  } catch {
    return null;
  }
};

function ValidationSummary({ contactErrors }) {
  const intl = useIntl();
  const { errors, submitCount } = useFormikContext();
  if (submitCount === 0) return null;
  const allErrors = [...Object.values(errors), ...(contactErrors || [])];
  if (allErrors.length === 0) return null;
  return (
    <Alert severity="error" sx={{ mb: 2 }} role="alert">
      <AlertTitle>{intl.formatMessage({ id: 'profile.validationSummary.title' })}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {allErrors.map((msg, i) => (
          <li key={i}>{msg}</li>
        ))}
      </Box>
    </Alert>
  );
}

function AutoPopulateFromZip({ filteredCities, targetZip, savedAlternateCityIds, arCities }) {
  const { values, setFieldValue } = useFormikContext();
  const altInitialized = useRef(false);

  // Canonical-name fallback: only fires when current_city is null (no saved city resolved
  // yet, or user just changed the zip). Never overrides a city already set by initialValues
  // (via enableReinitialize) or by the user picking from the dropdown.
  useEffect(() => {
    if (!filteredCities?.length || !targetZip || values.current_city !== null) return;
    const lookup = zipcodes.lookup(targetZip);
    if (!lookup) return;
    const byName = filteredCities.find((c) => c.city.toLowerCase() === lookup.city.toLowerCase());
    if (byName) {
      setFieldValue('current_city', byName, false);
      setFieldValue('current_state', byName.state_id || '', false);
    }
  }, [filteredCities, targetZip, values.current_city]);

  useEffect(() => {
    if (altInitialized.current || !savedAlternateCityIds?.length || !arCities?.length) return;
    const cities = savedAlternateCityIds.map((id) => arCities.find((c) => c.id === id)).filter(Boolean);
    if (cities.length) {
      setFieldValue('alternate_cities', cities, false);
      altInitialized.current = true;
    }
  }, [savedAlternateCityIds, arCities]);

  return null;
}

AutoPopulateFromZip.propTypes = {
  filteredCities: PropTypes.array,
  targetZip: PropTypes.string,
  savedAlternateCityIds: PropTypes.arrayOf(PropTypes.string),
  arCities: PropTypes.array,
};

function ScrollToErrorOnSubmit() {
  const { errors, submitCount, isSubmitting } = useFormikContext();
  useEffect(() => {
    if (submitCount > 0 && !isSubmitting && Object.keys(errors).length > 0) {
      const fieldIdMap = {
        current_city: 'where-located-onboard-dropdown',
        zip_code: 'zip-code-input',
        desired_commute_mi: 'travel-for-work-onboard-dropdown',
      };
      const firstErrorField = ['current_city', 'zip_code', 'desired_commute_mi'].find((f) => errors[f]);
      if (firstErrorField) {
        const el = document.getElementById(fieldIdMap[firstErrorField]);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [submitCount, isSubmitting]);
  return null;
}

const Profile = () => {
  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const updateUser = useUpdateUser();
  const history = useHistory();
  const queryClient = useQueryClient();

  const [choseToStay, setChoseToStay] = useState(false);
  const [preferencesFormDirty, setPreferencesFormDirty] = useState(false);
  const [jstOpen, setJstOpen] = useState(false);
  const [showJstConfirm, setShowJstConfirm] = useState(false);
  const contactFormRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [contactFormValidationErrors, setContactFormValidationErrors] = useState([]);
  const [cityFilterZip, setCityFilterZip] = useState(() => {
    const contactData = queryClient.getQueryData(['/users/me']);
    const zip = contactData?.zipcode || '';
    return /^\d{5}$/.test(zip) ? zip : '';
  });

  /*-- initial load --*/
  const experienceQuery = useQuery({
    queryKey: ['/users/me/experience/'],
    refetchOnMount: 'always',
    cacheTime: 0,
    refetchOnWindowFocus: false,
  });
  const contactQuery = useQuery({
    queryKey: ['/users/me'],
    cacheTime: 0,
    refetchOnWindowFocus: false,
  });

  const hasJstData = !!experienceQuery.data?.jst?.personal?.branch;
  const filteredCitiesQuery = useQuery({
    queryKey: [`/cities/us?zipcode=${cityFilterZip}&ordering=name`],
    enabled: !!(cityFilterZip && /^\d{5}$/.test(cityFilterZip)),
  });
  const arCitiesQuery = useQuery({
    queryKey: ['/cities/ar?ordering=name'],
  });
  const locationQuery = useQuery({
    queryKey: ['/users/me/location/'],
    cacheTime: 0,
    refetchOnWindowFocus: false,
    enabled: true,
  });

  const zipInitialized = useRef(false);
  useEffect(() => {
    if (zipInitialized.current) return;
    const zip = locationQuery.data?.zip_code || contactQuery.data?.zipcode || experienceQuery.data?.zipcode || '';
    if (/^\d{5}$/.test(zip)) {
      setCityFilterZip(zip);
      zipInitialized.current = true;
    }
  }, [locationQuery.data, contactQuery.data, experienceQuery.data]);

  const resolvedCurrentCity = (() => {
    const id = locationQuery.data?.current_city;
    if (!id || !filteredCitiesQuery.data?.length) return null;
    return filteredCitiesQuery.data.find((c) => String(c.id) === String(id)) ?? null;
  })();

  const initialValues = {
    current_city: resolvedCurrentCity,
    current_state: resolvedCurrentCity?.state_id || '',
    desired_commute_mi:
      locationQuery.data?.desired_commute_mi != null ? String(locationQuery.data.desired_commute_mi) : '',
    zip_code: locationQuery.data?.zip_code || contactQuery.data?.zipcode || experienceQuery.data?.zipcode || '',
    willing_to_relocate: locationQuery.data?.willing_to_relocate ? 'yes' : 'no',
    // Derived from the saved location so enableReinitialize restores the chips
    // after every save. Relocation cities are stored as names, and ARKANSAS_CITIES
    // uses the city name as its id, so resolve against that static list.
    alternate_cities: (locationQuery.data?.alternate_cities || [])
      .map((id) => ARKANSAS_CITIES.find((c) => c.id === id))
      .filter(Boolean),
    military_experience: experienceQuery.data?.current_military_service
      ? 'currently_enlisted'
      : experienceQuery.data?.previous_military_service
      ? 'previously_enlisted'
      : experienceQuery.data?.current_military_service === false &&
        experienceQuery.data?.previous_military_service === false
      ? 'no'
      : '',
    military_dependent:
      experienceQuery.data?.is_military_family_member === true
        ? 'yes'
        : experienceQuery.data?.is_military_family_member === false
        ? 'no'
        : '',
    amhi_enrolled:
      experienceQuery.data?.military?.amhi_enrolled === true
        ? 'yes'
        : experienceQuery.data?.military?.amhi_enrolled === false
        ? 'no'
        : '',
    enlistment_date: experienceQuery.data?.military?.enlistment_date ?? '',
    separation_date: experienceQuery.data?.military?.separation_date ?? '',
    discharge_status: experienceQuery.data?.military?.discharge_status ?? '',
    active_duty_war:
      experienceQuery.data?.military?.active_duty_war_campaign === true
        ? 'yes'
        : experienceQuery.data?.military?.active_duty_war_campaign === false
        ? 'no'
        : '',
    service_member_disabled:
      experienceQuery.data?.military?.disabled === true
        ? 'yes'
        : experienceQuery.data?.military?.disabled === false
        ? 'no'
        : '',
    va_compensation:
      experienceQuery.data?.military?.va_compensation_eligible === true
        ? 'yes'
        : experienceQuery.data?.military?.va_compensation_eligible === false
        ? 'no'
        : '',
    disability_rating: experienceQuery.data?.military?.disability_rating ?? '',
  };

  const [contactHasChanged, setContactHasChanged] = useState(false);

  const hasDirtyChanges = contactHasChanged || preferencesFormDirty;

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (contactHasChanged || preferencesFormDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [contactHasChanged, preferencesFormDirty]);

  /*-- analytics --*/
  useEffect(() => {
    document.title = intl.formatMessage({ id: 'profile.title' });
    TimeMe.stopTimer();
    TimeMe.setCurrentPageName('PROFILE');
    TimeMe.startTimer();
    return () => {
      dispatch(setOpenContactInfo(false));
    };
  }, []);

  return (
    <>
      <LeavingPageDialog
        when={contactHasChanged || preferencesFormDirty}
        onOK={() => {
          setPreferencesFormDirty(false);
          setContactHasChanged(false);
          return true;
        }}
        setChoseToStay={setChoseToStay}
        subtitle={intl.formatMessage({ id: 'profile.leavingDialog.subtitle', defaultMessage: 'You have unsaved changes on My Profile page. If you leave without saving them, your changes will be lost.' })}
      />
      <ConfirmationDialog
        open={showJstConfirm}
        title={intl.formatMessage({ id: 'leavingDialog.title' })}
        text={intl.formatMessage({ id: 'profile.leavingDialog.text', defaultMessage: 'You have unsaved changes on My Profile page. If you proceed without saving them, your changes will be lost.' })}
        cancelLabel={intl.formatMessage({ id: 'leavingDialog.stay' })}
        confirmLabel={intl.formatMessage({ id: 'leavingDialog.leave' })}
        onCancel={() => setShowJstConfirm(false)}
        onConfirm={() => {
          setShowJstConfirm(false);
          setJstOpen(true);
        }}
      />
      <div className={recStyles.content}>
        <Container maxWidth="lg">
          {experienceQuery.isInitialLoading || contactQuery.isInitialLoading ? (
            <LoadingSmall />
          ) : (
            <div>
              <Box>
                <Typography variant="h1">{intl.formatMessage({ id: 'profile.title' })}</Typography>
              </Box>

              <Divider sx={{ mt: 2, mb: 1 }} />
              <Typography variant="h4" component="h2" pt={2} pb={1}>
                {intl.formatMessage({ id: 'profile.aboutMe.title' })}
              </Typography>
              <Typography variant="body1" component="p">
                {intl.formatMessage({ id: 'profile.subtitle' })}
              </Typography>
              <Box m={3} />

              <Box mt={1} style={{ minWidth: 300 }}>
                <ContactForm
                  ref={contactFormRef}
                  initialValues={{
                    ...contactQuery.data,
                    degree: String(experienceQuery.data?.education?.[0]?.degree || ''),
                    zipcode:
                      contactQuery.data?.zipcode || experienceQuery.data?.zipcode || locationQuery.data?.zip_code || '',
                  }}
                  valueChanged={(hasItChanged) => setContactHasChanged(hasItChanged)}
                  choseToStay={choseToStay}
                  classNames={recStyles.educationForm}
                />

                <Typography sx={{ mt: 3 }} variant="h1">
                  {intl.formatMessage({ id: 'profile.myPreferences.title' })}
                </Typography>

                <Divider sx={{ mt: 2, mb: 1 }} />

                <Box mb={2}>
                  <FormLabel htmlFor="employer-match-switch">
                    <Typography variant="h4" component="h2" pt={2} pb={1}>
                      {intl.formatMessage({ id: 'profile.optIn.title' })}
                    </Typography>
                  </FormLabel>
                  <Typography variant="body1" component="p" sx={{ a: { color: 'primary.dark' }, mb: { xs: 1, md: 0 } }}>
                    {intl.formatMessage(
                      { id: 'profile.optIn.p1' },
                      {
                        experienceLink: (
                          <Link to={`${urlPrefix}/experience`}>
                            {intl.formatMessage({ id: 'layout.menu.experience' })}
                          </Link>
                        ),
                      }
                    )}
                  </Typography>
                  <Typography variant="body1" component="p" sx={{ mb: { xs: 1, md: 0 } }}>
                    {intl.formatMessage({ id: 'profile.optIn.p2' })}
                  </Typography>
                  <Typography variant="body1" component="p" sx={{ a: { color: 'primary.dark' }, mb: { xs: 1, md: 0 } }}>
                    {intl.formatMessage(
                      { id: 'profile.optIn.p3' },
                      {
                        termsLink: (
                          <Link to={`${urlPrefix}/terms-of-use`}>
                            {intl.formatMessage({ id: 'layout.footer.privacy' })}
                          </Link>
                        ),
                      }
                    )}
                  </Typography>
                  <Typography variant="body1" component="p">
                    {intl.formatMessage({ id: 'profile.optIn.p4' })}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                    <Typography>{intl.formatMessage({ id: 'profile.matchFeature.off' })}</Typography>
                    <ToggleSwitch
                      checked={
                        !contactQuery.isLoading && !contactQuery.isFetching
                          ? pathOr(false, ['data', 'user_match_feature'], contactQuery)
                          : false
                      }
                      onChange={({ target: { checked } }) => {
                        updateUser.mutate(
                          { user_match_feature: checked },
                          {
                            onSuccess: () => {
                              enqueueSnackbar('', {
                                variant: 'default',
                                content: (key) => (
                                  <CustomNotification id={key} check>
                                    {intl.formatMessage(
                                      {
                                        id: checked ? 'profile.matchFeature.optedIn' : 'profile.matchFeature.optedOut',
                                      },
                                      {
                                        link: (
                                          <Typography
                                            component="span"
                                            sx={{
                                              pb: 0.5,
                                              '&:active': { color: grey[100] },
                                              fontSize: '0.875rem',
                                              cursor: 'pointer',
                                              textDecoration: 'underline',
                                            }}
                                            onClick={() => history.push(`${urlPrefix}/my-profile`)}
                                          >
                                            {intl.formatMessage({ id: 'recommendations.updateContactInfo' })}
                                          </Typography>
                                        ),
                                      }
                                    )}
                                  </CustomNotification>
                                ),
                              });
                            },
                          }
                        );
                      }}
                      id="employer-match-switch"
                    />
                    <Typography>{intl.formatMessage({ id: 'profile.matchFeature.on' })}</Typography>
                  </Stack>
                </Box>

                <Box mb={2}>
                  <FormLabel htmlFor="email-switch">
                    <Typography variant="h4" component="h2" pt={2} pb={1}>
                      {intl.formatMessage({ id: 'profile.emailSettings.title' })}
                    </Typography>
                  </FormLabel>
                  <Typography variant="body1" component="p">
                    {intl.formatMessage({ id: 'profile.emailSettings.subtitle' })}
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                    <Typography>{intl.formatMessage({ id: 'profile.matchFeature.off' })}</Typography>
                    <ToggleSwitch
                      checked={
                        !contactQuery.isLoading && !contactQuery.isFetching
                          ? pathOr(false, ['data', 'bulk_email_enabled'], contactQuery)
                          : false
                      }
                      onChange={({ target: { checked } }) => {
                        updateUser.mutate({ bulk_email_enabled: checked });
                      }}
                      id="email-switch"
                    />
                    <Typography>{intl.formatMessage({ id: 'profile.matchFeature.on' })}</Typography>
                  </Stack>
                </Box>

                <Box mb={2}>
                  <Typography variant="h4" component="h2" pt={2} pb={1}>
                    {intl.formatMessage({ id: 'profile.location.title' })}
                  </Typography>

                  <Typography variant="body1" component="h3" pb={1}>
                    {intl.formatMessage({ id: 'profile.location.currentLocationPreferences' })}
                  </Typography>

                  <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validate={(values) => {
                      const errors = {};
                      // City - required
                      if (!values.current_city) {
                        errors.current_city = intl.formatMessage({ id: 'profile.location.form.cityRequired' });
                      }
                      // Zip code - required + Arkansas only
                      if (!values.zip_code) {
                        errors.zip_code = intl.formatMessage({ id: 'profile.location.form.zipCodeRequired' });
                      } else if (!/^\d{5}$/.test(values.zip_code)) {
                        errors.zip_code = intl.formatMessage({ id: 'experience.form.zipcodeFiveDigits' });
                      }
                      // Commute distance - required (0 = "Only in my city" is valid, so check for empty string only)
                      if (values.desired_commute_mi === '' || values.desired_commute_mi == null) {
                        errors.desired_commute_mi = intl.formatMessage({ id: 'profile.location.form.commuteRequired' });
                      }
                      const showAdditional = values.military_experience && values.military_experience !== 'no';
                      if (showAdditional) {
                        const isCurrentlyEnlisted = values.military_experience === 'currently_enlisted';
                        const isPreviouslyEnlisted = values.military_experience === 'previously_enlisted';

                        // Enlistment date - required for both currently and previously enlisted
                        if ((isCurrentlyEnlisted || isPreviouslyEnlisted) && !values.enlistment_date) {
                          errors.enlistment_date = intl.formatMessage({
                            id: 'profile.military.validation.enlistmentDateRequired',
                          });
                        } else if (values.enlistment_date) {
                          const enlistErr = validateMilitaryDate(values.enlistment_date, intl);
                          if (enlistErr) errors.enlistment_date = enlistErr;
                        }

                        // Separation date - required when previously enlisted
                        if (isPreviouslyEnlisted && !values.separation_date) {
                          errors.separation_date = intl.formatMessage({
                            id: 'profile.military.validation.separationDateRequired',
                          });
                        } else if (values.separation_date) {
                          const sepErr = validateMilitaryDate(values.separation_date, intl);
                          if (sepErr) {
                            errors.separation_date = sepErr;
                          } else if (
                            values.enlistment_date &&
                            new Date(values.separation_date) < new Date(values.enlistment_date)
                          ) {
                            errors.separation_date = intl.formatMessage({
                              id: 'profile.military.validation.separationBeforeEnlistment',
                            });
                          }
                        }

                        // Discharge status - required when previously enlisted
                        if (isPreviouslyEnlisted && !values.discharge_status) {
                          errors.discharge_status = intl.formatMessage({
                            id: 'profile.military.validation.dischargeStatusRequired',
                          });
                        }

                        if (values.service_member_disabled === 'yes') {
                          if (values.disability_rating) {
                            const n = Number(values.disability_rating);
                            if (n < 0 || n > 100 || !Number.isInteger(n)) {
                              errors.disability_rating = intl.formatMessage({
                                id: 'profile.military.validation.ratingRange',
                              });
                            }
                          }
                        }
                      }
                      return errors;
                    }}
                    onSubmit={(values, { setFieldError, setFieldTouched }) => {
                      // Validate ContactForm first
                      if (contactFormRef.current) {
                        const contactErrors = contactFormRef.current.validate();
                        if (contactErrors) {
                          setContactFormValidationErrors(contactFormRef.current.getErrors());
                          return;
                        }
                      }
                      setContactFormValidationErrors([]);
                      setSubmitting(true);

                      // Submit contact form data (About Me section)
                      const contactPromise = contactFormRef.current
                        ? contactFormRef.current.submit()
                        : Promise.resolve();

                      const valuesToSubmit = {
                        current_city: values.current_city.id,
                        ...(values.zip_code ? { zip_code: values.zip_code } : {}),
                        ...(Number.isInteger(Number(values.desired_commute_mi))
                          ? { desired_commute_mi: Number(values.desired_commute_mi) }
                          : {}),
                        ...(values.willing_to_relocate
                          ? { willing_to_relocate: values.willing_to_relocate === 'yes' }
                          : {}),
                        ...(values.alternate_cities?.length
                          ? { alternate_cities: values.alternate_cities.map(({ id }) => id) }
                          : {}),
                      };
                      const showAdditional = values.military_experience && values.military_experience !== 'no';
                      const existingData = experienceQuery.data || {};
                      const militaryPayload = {
                        job_experience: existingData.job_experience || [],
                        education: existingData.education || [],
                        current_military_service: values.military_experience === 'currently_enlisted',
                        previous_military_service: values.military_experience === 'previously_enlisted',
                        is_military_family_member: values.military_dependent === 'yes',
                        military: {
                          personal_status:
                            values.military_experience === 'currently_enlisted'
                              ? 'yes_currently_enlisted'
                              : values.military_experience === 'previously_enlisted'
                              ? 'yes_previously_enlisted'
                              : 'no',
                          is_dependent_or_family: values.military_dependent === 'yes',
                          ...(showAdditional
                            ? {
                                enlistment_date: formatDateForAPI(values.enlistment_date),
                                separation_date:
                                  values.military_experience === 'currently_enlisted'
                                    ? null
                                    : formatDateForAPI(values.separation_date),
                                discharge_status: values.discharge_status || null,
                                active_duty_war_campaign: values.active_duty_war === 'yes',
                                disabled: values.service_member_disabled === 'yes',
                                va_compensation_eligible: values.va_compensation === 'yes',
                                disability_rating: values.disability_rating ? Number(values.disability_rating) : null,
                                amhi_enrolled: values.amhi_enrolled === 'yes',
                              }
                            : {
                                enlistment_date: null,
                                separation_date: null,
                                discharge_status: null,
                                active_duty_war_campaign: false,
                                disabled: false,
                                va_compensation_eligible: false,
                                disability_rating: null,
                                amhi_enrolled: false,
                              }),
                        },
                      };
                      const hasMilitaryData = values.military_experience || values.military_dependent;
                      Promise.all([
                        contactPromise,
                        apiRequest('PUT', '/users/me/location/', {
                          data: valuesToSubmit,
                        }),
                      ])
                        .then(() => {
                          if (hasMilitaryData) {
                            return apiRequest('PUT', '/users/me/experience/', {
                              data: militaryPayload,
                            });
                          }
                        })
                        .then(() => {
                          setSubmitting(false);
                          setPreferencesFormDirty(false);
                          setContactHasChanged(false);
                          setContactFormValidationErrors([]);
                          history.block(() => {});
                          queryClient.invalidateQueries({ queryKey: ['/users/me'] });
                          queryClient.invalidateQueries({ queryKey: ['/users/me/location/'] });
                          if (hasMilitaryData) {
                            queryClient.invalidateQueries({ queryKey: ['/users/me/experience/'] });
                          }
                          enqueueSnackbar('', {
                            variant: 'default',
                            content: (key) => (
                              <CustomNotification id={key} check>
                                {intl.formatMessage({ id: 'experience.changesSaved' })}
                              </CustomNotification>
                            ),
                          });
                          if (values.military_experience === 'no' && values.military_dependent === 'no') {
                            history.push(`${urlPrefix}/experience`);
                          }
                        })
                        .catch(() => {
                          setSubmitting(false);
                          if (process.env.NODE_ENV === 'development') {
                            console.error('Error saving profile data:', error);
                          }
                        });
                    }}
                  >
                    {({ values, errors, touched, handleSubmit, setFieldValue: baseSetFieldValue, setFieldTouched }) => {
                      const setFieldValue = (...args) => {
                        setPreferencesFormDirty(true);
                        baseSetFieldValue(...args);
                      };
                      return (
                        <Box component="form" onSubmit={handleSubmit}>
                          <ScrollToErrorOnSubmit />
                          <AutoPopulateFromZip
                            filteredCities={filteredCitiesQuery.data}
                            targetZip={cityFilterZip}
                            savedAlternateCityIds={locationQuery.data?.alternate_cities}
                            arCities={arCitiesQuery.data ?? []}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              gap: { xs: 1, md: 3 },
                              mb: 1,
                              flexDirection: { xs: 'column', md: 'row' },
                              alignItems: 'flex-end',
                              maxWidth: 750,
                            }}
                          >
                            {/* Zip Code */}
                            <TextField
                              fullWidth
                              variant="filled"
                              label={
                                <>
                                  {intl.formatMessage({ id: 'profile.location.form.zipCode' })}
                                  <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
                                </>
                              }
                              name="zip_code"
                              value={values.zip_code}
                              onBlur={() => setFieldTouched('zip_code', true)}
                              onChange={(e) => {
                                const newZip = e.target.value;
                                setFieldValue('zip_code', newZip, false);
                                if (/^\d{5}$/.test(newZip)) {
                                  setCityFilterZip(newZip);
                                  setFieldValue('current_city', null, false);
                                  setFieldValue('current_state', '', false);
                                } else {
                                  setCityFilterZip('');
                                  setFieldValue('current_city', null, false);
                                  setFieldValue('current_state', '', false);
                                }
                              }}
                              error={!!touched.zip_code && !!errors.zip_code}
                              helperText={touched.zip_code && errors.zip_code ? errors.zip_code : ''}
                              id="zip-code-input"
                              InputProps={{
                                inputComponent: ZipcodeInput,
                              }}
                            />

                            <Autocomplete
                              id="where-located-onboard-dropdown"
                              fullWidth
                              disabled={!cityFilterZip}
                              options={filteredCitiesQuery.data ?? []}
                              getOptionLabel={({ city }) => city}
                              getOptionKey={({ id }) => id}
                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                              value={values.current_city ?? null}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="filled"
                                  label={
                                    <>
                                      {intl.formatMessage({ id: 'profile.location.form.cityLabel' })}
                                      <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
                                    </>
                                  }
                                  helperText={touched.current_city && errors.current_city ? errors.current_city : ''}
                                  error={touched.current_city && Boolean(errors.current_city)}
                                  id="where-located-onboard-dropdown"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: filteredCitiesQuery.isLoading ? (
                                      <InputAdornment position="end">
                                        <CircularProgress color="inherit" size={20} />
                                      </InputAdornment>
                                    ) : (
                                      params.InputProps.endAdornment
                                    ),
                                  }}
                                />
                              )}
                              sx={{
                                '& .MuiInputBase-root': {
                                  paddingTop: '18px!important',
                                },
                                '& .MuiAutocomplete-input': {
                                  fontSize: 12,
                                },
                              }}
                              onChange={(_, value) => {
                                setFieldValue('current_city', value, false);
                                setFieldValue('current_state', value?.state_id || '', false);
                              }}
                            />

                            {/* State, auto-populated from city */}
                            <TextField
                              variant="filled"
                              label={intl.formatMessage({ id: 'profile.location.form.state' })}
                              value={values.current_state}
                              InputProps={{ readOnly: true }}
                              sx={{ minWidth: { xs: '100%', md: 80 }, maxWidth: { xs: '100%', md: 100 } }}
                              inputProps={{ 'aria-label': intl.formatMessage({ id: 'profile.location.form.state' }) }}
                            />
                          </Box>

                          {/* How far willing to drive */}
                          <FormControl
                            fullWidth
                            error={!!touched.desired_commute_mi && !!errors.desired_commute_mi}
                            variant="filled"
                            sx={{ mb: 2 }}
                          >
                            <Typography variant="body1" pb={1}>
                              {intl.formatMessage({ id: 'profile.location.form.driveQuestion' })}
                              <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
                            </Typography>
                            <Select
                              sx={{ maxWidth: 250 }}
                              labelId="travel-distance-label"
                              value={values.desired_commute_mi}
                              onChange={(evt) => setFieldValue('desired_commute_mi', evt.target.value)}
                              id="travel-for-work-onboard-dropdown"
                              variant="filled"
                              MenuProps={{
                                disableScrollLock: true,
                              }}
                              onKeyDown={preventSubmitOnEnter}
                            >
                              {DISTANCE_OPTIONS.map((opt) => (
                                <MenuItem
                                  key={opt.id}
                                  value={opt.id}
                                  id={`travel-for-work-${opt.miles}-miles-dropdown`}
                                >
                                  {opt.labelId ? (
                                    <em>{intl.formatMessage({ id: opt.labelId })}</em>
                                  ) : (
                                    `${intl.formatMessage({ id: 'onboarding.location.within' })} ${
                                      opt.miles
                                    } ${intl.formatMessage({ id: 'onboarding.location.miles' })}`
                                  )}
                                </MenuItem>
                              ))}
                            </Select>
                            {!!touched.desired_commute_mi && !!errors.desired_commute_mi && (
                              <FormHelperText>{errors.desired_commute_mi}</FormHelperText>
                            )}
                          </FormControl>
                          <Typography variant="h5" component="h3" pb={1}>
                            {intl.formatMessage({ id: 'profile.location.relocationPreferences' })}
                          </Typography>

                          {/* Willing to relocate */}
                          <YesNoRadioGroup
                            name="willing_to_relocate"
                            label={
                              <>
                                {intl.formatMessage({ id: 'onboarding.location.willingToRelocate' })}
                                <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
                              </>
                            }
                            ariaLabel={intl.formatMessage({ id: 'onboarding.location.willingToRelocate' })}
                            value={values.willing_to_relocate}
                            onChange={(val) => {
                              setFieldValue('willing_to_relocate', val);
                              if (val === 'no') setFieldValue('alternate_cities', []);
                            }}
                            error={!!touched.willing_to_relocate && !!errors.willing_to_relocate}
                            helperText={errors.willing_to_relocate}
                            yesId="yes-relocate-onboard-radio-button"
                            noId="no-relocate-onboard-radio-button"
                            sx={{ mb: 1 }}
                            helperTextSx={{ left: 0, right: 'auto' }}
                          />

                          {/* Cities to relocate */}
                          <Box
                            sx={{
                              display: values.willing_to_relocate === 'yes' ? 'flex' : 'none',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography sx={{ fontWeight: 700, mb: 1 }}>
                              {intl.formatMessage({ id: 'onboarding.location.relocationCityLabel' })}
                            </Typography>
                            <Autocomplete
                              multiple
                              id="relocation-city-onboard-dropdown"
                              options={arCitiesQuery.data ?? ARKANSAS_CITIES}
                              getOptionLabel={({ city }) => city}
                              getOptionKey={({ id }) => id}
                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                              value={values.alternate_cities}
                              renderTags={(value, getTagProps) => {
                                return (
                                  <div>
                                    {value.map((option, index) => {
                                      if (!option) return null;
                                      const { className, ...rest } = getTagProps({ index }); //eslint-disable-line

                                      return (
                                        <Chip
                                          key={option.id ?? index}
                                          size="small"
                                          label={option.city ?? ''}
                                          {...rest}
                                          sx={{ maxWidth: 180, px: 0, my: 0.5 }}
                                        />
                                      );
                                    })}
                                  </div>
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="filled"
                                  placeholder={intl.formatMessage({
                                    id: 'onboarding.location.relocationCityPlaceholder',
                                  })}
                                  id="relocation-city-onboard-dropdown"
                                  helperText={
                                    touched.alternate_cities && errors.alternate_cities ? errors.alternate_cities : ''
                                  }
                                  error={touched.alternate_cities && Boolean(errors.alternate_cities)}
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: arCitiesQuery.isLoading ? (
                                      <InputAdornment position="end">
                                        <CircularProgress color="inherit" size={20} />
                                      </InputAdornment>
                                    ) : (
                                      params.InputProps.endAdornment
                                    ),
                                  }}
                                />
                              )}
                              onChange={(_, value) => setFieldValue('alternate_cities', value)}
                              sx={{
                                maxWidth: { xs: '100%', sm: 400 },
                                '& .MuiInputBase-root': {
                                  paddingTop: '18px!important',
                                },
                                '& .MuiAutocomplete-input': {
                                  fontSize: 12,
                                },
                              }}
                            />
                          </Box>

                          {/* Military Experience */}
                          <Typography variant="h5" component="h3" pb={1} pt={3}>
                            {intl.formatMessage({ id: 'profile.military.title' })}
                          </Typography>

                          {/* Q1: Have you served or are you currently serving? */}
                          <FormControl
                            sx={{ mb: 2 }}
                            error={!!touched.military_experience && !!errors.military_experience}
                          >
                            <FormLabel component="legend" sx={{ '&.Mui-focused': { color: 'text.primary' } }}>
                              {intl.formatMessage({ id: 'profile.military.servedQuestion' })}
                            </FormLabel>
                            <RadioGroup
                              aria-label={intl.formatMessage({ id: 'profile.military.servedQuestion' })}
                              name="military_experience"
                              value={values.military_experience}
                              onChange={(event) => {
                                const val = event.target.value;
                                setFieldValue('military_experience', val);
                                if (val === 'no') {
                                  setFieldValue('enlistment_date', '');
                                  setFieldValue('separation_date', '');
                                  setFieldValue('discharge_status', '');
                                  setFieldValue('active_duty_war', '');
                                  setFieldValue('service_member_disabled', '');
                                  setFieldValue('va_compensation', '');
                                  setFieldValue('disability_rating', '');
                                  setFieldValue('amhi_enrolled', '');
                                }
                              }}
                              sx={{
                                flexDirection: { xs: 'column', sm: 'row' },
                                flexWrap: 'wrap',
                                rowGap: 0.5,
                                columnGap: 2,
                              }}
                            >
                              <FormControlLabel
                                value="currently_enlisted"
                                control={<Radio color="primary" id="military-currently-enlisted-radio" />}
                                label={intl.formatMessage({ id: 'profile.military.currentlyEnlisted' })}
                              />
                              <FormControlLabel
                                value="previously_enlisted"
                                control={<Radio color="primary" id="military-previously-enlisted-radio" />}
                                label={intl.formatMessage({ id: 'profile.military.previouslyEnlisted' })}
                              />
                              <FormControlLabel
                                value="no"
                                control={<Radio color="primary" id="military-no-radio" />}
                                label={intl.formatMessage({ id: 'jobSearchActivity.form.no' })}
                              />
                            </RadioGroup>
                            {!!touched.military_experience && !!errors.military_experience && (
                              <FormHelperText>{errors.military_experience}</FormHelperText>
                            )}
                          </FormControl>

                          {/* Q2: Are you the dependant, spouse, widow, or caretaker? */}
                          <YesNoRadioGroup
                            name="military_dependent"
                            label={intl.formatMessage({ id: 'profile.military.dependentQuestion' })}
                            value={values.military_dependent}
                            onChange={(val) => setFieldValue('military_dependent', val)}
                            error={!!touched.military_dependent && !!errors.military_dependent}
                            helperText={errors.military_dependent}
                            yesId="military-dependent-yes-radio"
                            noId="military-dependent-no-radio"
                          />

                          {/* Additional Military Questions - show when Q1 != 'no' */}
                          {values.military_experience && values.military_experience !== 'no' ? (
                            <Box>
                              {/* Q3: AMHI Enrollment */}
                              <YesNoRadioGroup
                                name="amhi_enrolled"
                                label={intl.formatMessage({ id: 'profile.military.amhiQuestion' })}
                                value={values.amhi_enrolled}
                                onChange={(val) => setFieldValue('amhi_enrolled', val)}
                                error={!!touched.amhi_enrolled && !!errors.amhi_enrolled}
                                helperText={errors.amhi_enrolled}
                                yesId="amhi-yes-radio"
                                noId="amhi-no-radio"
                              />

                              {/* Q4: Enlistment Date */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                                <Typography variant="body1" sx={{ minWidth: 180 }}>
                                  {intl.formatMessage({ id: 'profile.military.enlistmentDate' })}
                                  <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
                                </Typography>
                                <DatePicker
                                  label="MM/DD/YYYY"
                                  value={values.enlistment_date ? new Date(values.enlistment_date) : null}
                                  minDate={new Date(1900, 0, 1)}
                                  maxDate={new Date()}
                                  onChange={(date) => setFieldValue('enlistment_date', date, false)}
                                  slotProps={{
                                    textField: {
                                      variant: 'filled',
                                      id: 'military-enlistment-date',
                                      sx: { maxWidth: 200, '& .MuiFormHelperText-root': { whiteSpace: 'nowrap' } },
                                      onBlur: () => setFieldTouched('enlistment_date', true),
                                      error: !!touched.enlistment_date && !!errors.enlistment_date,
                                      helperText:
                                        touched.enlistment_date && errors.enlistment_date ? errors.enlistment_date : '',
                                    },
                                  }}
                                />
                              </Box>

                              {/* Q5: Separation Date - hide when currently enlisted */}
                              {values.military_experience !== 'currently_enlisted' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                                  <Typography variant="body1" sx={{ minWidth: 180 }}>
                                    {intl.formatMessage({ id: 'profile.military.separationDate' })}
                                    {values.military_experience === 'previously_enlisted' && (
                                      <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
                                    )}
                                  </Typography>
                                  <DatePicker
                                    label="MM/DD/YYYY"
                                    value={values.separation_date ? new Date(values.separation_date) : null}
                                    minDate={
                                      values.enlistment_date ? new Date(values.enlistment_date) : new Date(1900, 0, 1)
                                    }
                                    maxDate={new Date()}
                                    onChange={(date) => setFieldValue('separation_date', date, false)}
                                    slotProps={{
                                      textField: {
                                        variant: 'filled',
                                        id: 'military-separation-date',
                                        sx: { maxWidth: 200, '& .MuiFormHelperText-root': { whiteSpace: 'nowrap' } },
                                        onBlur: () => setFieldTouched('separation_date', true),
                                        error: !!touched.separation_date && !!errors.separation_date,
                                        helperText:
                                          touched.separation_date && errors.separation_date
                                            ? errors.separation_date
                                            : '',
                                      },
                                    }}
                                  />
                                </Box>
                              )}

                              {/* Q6: Discharge Status */}
                              {values.military_experience !== 'currently_enlisted' && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                                  <Typography variant="body1" sx={{ minWidth: 180, pt: 1.5 }}>
                                    {intl.formatMessage({ id: 'profile.military.dischargeStatus' })}
                                    {values.military_experience === 'previously_enlisted' && (
                                      <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
                                    )}
                                  </Typography>
                                  <FormControl
                                    variant="filled"
                                    sx={{ maxWidth: 250 }}
                                    error={!!touched.discharge_status && !!errors.discharge_status}
                                  >
                                    <Select
                                      id="military-discharge-status"
                                      name="discharge_status"
                                      value={values.discharge_status}
                                      onChange={(e) => setFieldValue('discharge_status', e.target.value)}
                                      onBlur={() => setFieldTouched('discharge_status', true)}
                                      displayEmpty
                                      renderValue={(selected) => {
                                        if (!selected) {
                                          return (
                                            <em style={{ color: '#aaa', fontStyle: 'normal' }}>
                                              {intl.formatMessage({ id: 'profile.military.dischargeStatus' })}
                                            </em>
                                          );
                                        }
                                        const option = DISCHARGE_STATUS_OPTIONS.find((opt) => opt.value === selected);
                                        return option ? intl.formatMessage({ id: option.labelId }) : selected;
                                      }}
                                    >
                                      {DISCHARGE_STATUS_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                          {intl.formatMessage({ id: opt.labelId })}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {!!touched.discharge_status && !!errors.discharge_status && (
                                      <FormHelperText>{errors.discharge_status}</FormHelperText>
                                    )}
                                  </FormControl>
                                </Box>
                              )}

                              {/* Q7: Active Duty War */}
                              <YesNoRadioGroup
                                name="active_duty_war"
                                label={
                                  <>
                                    {intl.formatMessage({ id: 'profile.military.activeDutyQuestion1' })}
                                    <a
                                      href="http://www.opm.gov/policy-data-oversight/veterans-services/vetguide"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#1a0dab' }}
                                    >
                                      {intl.formatMessage({ id: 'profile.military.activeDutyLink' })}
                                    </a>
                                  </>
                                }
                                ariaLabel={intl.formatMessage({ id: 'profile.military.activeDutyQuestion1' })}
                                value={values.active_duty_war}
                                onChange={(val) => setFieldValue('active_duty_war', val)}
                                error={!!touched.active_duty_war && !!errors.active_duty_war}
                                helperText={errors.active_duty_war}
                                yesId="active-duty-war-yes-radio"
                                noId="active-duty-war-no-radio"
                              />

                              {/* Q8: Service Member Disabled */}
                              <YesNoRadioGroup
                                name="service_member_disabled"
                                label={intl.formatMessage({ id: 'profile.military.disabledQuestion' })}
                                value={values.service_member_disabled}
                                onChange={(val) => {
                                  setFieldValue('service_member_disabled', val);
                                  if (val === 'no') {
                                    setFieldValue('va_compensation', '');
                                    setFieldValue('disability_rating', '');
                                  }
                                }}
                                error={!!touched.service_member_disabled && !!errors.service_member_disabled}
                                helperText={errors.service_member_disabled}
                                yesId="disabled-yes-radio"
                                noId="disabled-no-radio"
                              />

                              {/* Q9 & Q10 - Only show when Q8 = 'yes' */}
                              {values.service_member_disabled === 'yes' && (
                                <Box sx={{ pl: 4 }}>
                                  {/* Q9: VA Compensation */}
                                  <YesNoRadioGroup
                                    name="va_compensation"
                                    label={intl.formatMessage({ id: 'profile.military.vaCompensationQuestion' })}
                                    value={values.va_compensation}
                                    onChange={(val) => {
                                      setFieldValue('va_compensation', val);
                                      if (val === 'no') setFieldValue('disability_rating', '');
                                    }}
                                    error={!!touched.va_compensation && !!errors.va_compensation}
                                    helperText={errors.va_compensation}
                                    yesId="va-compensation-yes-radio"
                                    noId="va-compensation-no-radio"
                                    sx={{ mb: 2, width: '100%' }}
                                    labelSx={{ width: '100%' }}
                                  />

                                  {/* Q10: Disability Rating - only show when Q9 = 'yes' */}
                                  {values.va_compensation === 'yes' && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="body1" sx={{ mb: 1, width: '100%' }}>
                                        {intl.formatMessage({ id: 'profile.military.disabilityRatingQuestion' })}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                          variant="standard"
                                          name="disability_rating"
                                          type="number"
                                          value={values.disability_rating}
                                          onChange={(e) => setFieldValue('disability_rating', e.target.value)}
                                          InputProps={{
                                            inputProps: { min: 0, max: 100, step: 1 },
                                          }}
                                          error={!!touched.disability_rating && !!errors.disability_rating}
                                          helperText={
                                            touched.disability_rating && errors.disability_rating
                                              ? errors.disability_rating
                                              : ''
                                          }
                                          sx={{ width: 120, '& input': { textAlign: 'center' } }}
                                          id="military-disability-rating"
                                        />
                                        <Typography variant="body1">%</Typography>
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              )}

                              {/* Joint Services Transcript Information */}
                              <Typography variant="h5" component="h3" pb={1} pt={2}>
                                {intl.formatMessage({ id: 'profile.military.jstTitle' })}
                              </Typography>

                              <Typography variant="subtitle2" component="h4" pb={0.5}>
                                {intl.formatMessage({ id: 'profile.military.jstWhat' })}
                              </Typography>
                              <Typography variant="body2" pb={2}>
                                {intl.formatMessage({ id: 'profile.military.jstDescription' })}
                              </Typography>

                              <Typography variant="subtitle2" component="h4" pb={0.5}>
                                {intl.formatMessage({ id: 'profile.military.jstHow' })}
                              </Typography>
                              <Typography variant="body2" pb={2}>
                                {intl.formatMessage({ id: 'profile.military.jstAccess1' })}
                                <a
                                  href="https://jst.doded.mil/jst/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#1a0dab' }}
                                >
                                  {intl.formatMessage({ id: 'profile.military.jstAccessLink1' })}
                                </a>
                                {intl.formatMessage({ id: 'profile.military.jstAccess2' })}
                                <a
                                  href="https://www.airuniversity.af.edu/Registrar/Transcript-Requests/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#1a0dab' }}
                                >
                                  {intl.formatMessage({ id: 'profile.military.jstAccessLink2' })}
                                </a>
                              </Typography>

                              <Typography variant="body2" pb={2}>
                                {intl.formatMessage({
                                  id: hasJstData ? 'profile.military.jstViewEditPrompt' : 'profile.military.jstPrompt',
                                })}
                              </Typography>

                              <Button
                                variant="contained"
                                color="primary"
                                id="enter-jst-button"
                                sx={{ mb: 2 }}
                                onClick={() => {
                                  if (hasDirtyChanges) {
                                    setShowJstConfirm(true);
                                  } else {
                                    setJstOpen(true);
                                  }
                                }}
                              >
                                {intl.formatMessage({
                                  id: hasJstData ? 'profile.military.viewEditJst' : 'profile.military.enterJst',
                                })}
                              </Button>
                            </Box>
                          ) : null}

                          <ValidationSummary contactErrors={contactFormValidationErrors} />

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <Button
                              color={'secondary'}
                              disabled={submitting}
                              size="large"
                              type="submit"
                              variant="contained"
                              id="next-onboard-step-5-button"
                            >
                              {submitting ? (
                                <div className="spinner-border text-primary" role="status">
                                  <span className="sr-only">{intl.formatMessage({ id: 'forms.signIn.loading' })}</span>
                                </div>
                              ) : (
                                intl.formatMessage({ id: 'profile.location.form.save' })
                              )}
                            </Button>
                          </Box>
                        </Box>
                      );
                    }}
                  </Formik>
                </Box>
              </Box>
            </div>
          )}
        </Container>
      </div>
      <Dialog open={jstOpen} fullWidth maxWidth="lg" disableEscapeKeyDown PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogContent sx={{ p: 0 }}>
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>Loading...</Box>}>
            <JSTMain
              onClose={() => {
                setJstOpen(false);
                setPreferencesFormDirty(false);
                setContactHasChanged(false);
              }}
            />
          </Suspense>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ContactForm = forwardRef(function ContactForm(
  { classNames, valueChanged = () => {}, choseToStay, initialValues: defaultValues },
  ref
) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const educationOptions = [
    { value: '11', title: intl.formatMessage({ id: 'forms.onboarding.education.noDiploma' }) },
    { value: '11.5', title: intl.formatMessage({ id: 'forms.onboarding.education.ged' }) },
    { value: '12', title: intl.formatMessage({ id: 'forms.onboarding.education.highSchoolDiploma' }) },
    { value: '13', title: intl.formatMessage({ id: 'forms.onboarding.education.someCollege' }) },
    { value: '15', title: intl.formatMessage({ id: 'forms.onboarding.education.certificateProficiency' }) },
    { value: '18', title: intl.formatMessage({ id: 'forms.onboarding.education.technicalCertificate' }) },
    { value: '14', title: intl.formatMessage({ id: 'forms.onboarding.education.assocDegree' }) },
    { value: '19', title: intl.formatMessage({ id: 'forms.onboarding.education.advancedCertificate' }) },
    { value: '16', title: intl.formatMessage({ id: 'forms.onboarding.education.bachDegree' }) },
    { value: '21', title: intl.formatMessage({ id: 'forms.onboarding.education.postBachelorsCertificate' }) },
    { value: '17', title: intl.formatMessage({ id: 'forms.onboarding.education.mastersDegree' }) },
    { value: '16.5', title: intl.formatMessage({ id: 'forms.onboarding.education.graduateCertificate' }) },
    { value: '22', title: intl.formatMessage({ id: 'forms.onboarding.education.doctoralProfessional' }) },
    { value: '20', title: intl.formatMessage({ id: 'forms.onboarding.education.doctoralResearch' }) },
  ];

  const lettersNumbers = /^[^<>]+$/; // eslint-disable-line no-useless-escape
  //eslint-disable-next-line
  const phoneRegExp = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4}$/;
  const emailRegExp = /^\S+@\S+\.\w\w+$/;
  const validationSchema = yup.object({
    first_name: yup
      .string('forms.onboarding.firstNameValidation')
      .min(2, 'forms.onboarding.firstNameMinLength')
      .max(50, 'forms.onboarding.firstNameMaxLength')
      .matches(lettersNumbers, 'profile.validation.basicPunctiation')
      .required('forms.onboarding.firstNameRequired'),
    last_name: yup
      .string('forms.onboarding.lastNameValidation')
      .min(2, 'forms.onboarding.lastNameMinLength')
      .max(50, 'forms.onboarding.lastNameMaxLength')
      .matches(lettersNumbers, 'profile.validation.basicPunctiation')
      .required('forms.onboarding.lastNameRequired'),
    email: yup
      .string('forms.onboarding.emailRequired')
      .matches(emailRegExp, 'forms.onboarding.emailValidation')
      .required('forms.onboarding.emailRequired'),
    phone_number: yup
      .string('forms.onboarding.phoneNumber')
      .matches(phoneRegExp, { message: 'forms.onboarding.exactlyXDigits', excludeEmptyString: true }),
    zipcode: yup
      .string('forms.onboarding.zipcode')
      .test(
        'valid-us-zip',
        intl.formatMessage({ id: 'experience.form.zipcodeInvalidUS' }),
        (value) => !value || value.length < 5 || zipcodes.lookup(value) !== undefined
      ),
  });

  const initialValues = {
    first_name: propOr('', 'first_name', defaultValues),
    last_name: propOr('', 'last_name', defaultValues),
    email: propOr('', 'email', defaultValues),
    phone_number: propOr('', 'phone_number', defaultValues),
    zipcode: propOr('', 'zipcode', defaultValues),
    degree: propOr('', 'degree', defaultValues),
  };

  const [keysWithChanges, setKeysWithChanges] = useState([]);

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: () => {},
  });

  useImperativeHandle(ref, () => ({
    validate: () => {
      const errors = validationSchema.validateSync
        ? (() => {
            try {
              validationSchema.validateSync(formik.values, { abortEarly: false });
              return null;
            } catch (err) {
              formik.setTouched(Object.fromEntries(err.inner.map((e) => [e.path, true])));
              formik.setErrors(Object.fromEntries(err.inner.map((e) => [e.path, e.message])));
              return true;
            }
          })()
        : null;
      return errors;
    },
    getErrors: () => {
      try {
        validationSchema.validateSync(formik.values, { abortEarly: false });
        return [];
      } catch (err) {
        return err.inner.map((e) => intl.formatMessage({ id: e.message }, { amount: 10 }));
      }
    },
    submit: () => {
      const { first_name, last_name, phone_number, zipcode, degree } = formik.values;
      const promises = [];
      promises.push(
        updateUserData({
          first_name,
          last_name,
          phone_number,
          zipcode,
          preferred_name: first_name || '',
        }).then(() => {
          dispatch(setPreferredName(first_name));
          dispatch(setZipcode(zipcode));
          valueChanged(false);
          setKeysWithChanges([]);
        })
      );
      if (degree) {
        promises.push(updateExperience({ education: [{ degree }] }));
      }
      return Promise.all(promises);
    },
  }));

  const [initialFormikValues, setInitialFormikValues] = useState({ ...formik.values });

  useEffect(() => {
    if (!formik.values.empty && initialFormikValues.empty) {
      setInitialFormikValues({ ...formik.values });
    }
  }, [formik.values]);

  const handleDiffs = (fieldName, evt) => {
    const initialValues = initialFormikValues;
    const currentValues = { ...formik.values, [fieldName]: evt.target.value };
    if (equals(initialValues, currentValues)) {
      setKeysWithChanges([]);
      valueChanged(false);
    } else {
      const differentKeys = [];
      for (const key in initialValues) {
        if (initialValues[key] !== currentValues[key]) {
          differentKeys.push(key);
        }
      }
      setKeysWithChanges(differentKeys);
      valueChanged(true);
    }
  };

  return (
    <div className={classNames}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'auto', md: '1fr 1fr' },
          gap: { xs: '20px', md: '16px 40px' },
        }}
      >
        <TextField
          sx={{
            '.MuiFilledInput-input': { borderRadius: '4px 4px 0 0 !important' },
          }}
          error={
            (formik.touched.first_name && Boolean(formik.errors.first_name)) ||
            (choseToStay && keysWithChanges.includes('first_name'))
          }
          fullWidth
          helperText={
            formik.touched.first_name &&
            formik.errors.first_name &&
            intl.formatMessage({ id: formik.errors.first_name })
          }
          label={
            <>
              {intl.formatMessage({ id: 'forms.onboarding.firstName' })}
              <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
            </>
          }
          name="first_name"
          onBlur={formik.handleBlur}
          onChange={(e) => {
            formik.handleChange(e);
            handleDiffs('first_name', e);
          }}
          onKeyDown={preventSubmitOnEnter}
          type="text"
          value={formik.values.first_name}
          variant="filled"
          inputProps={{
            title: intl.formatMessage({ id: 'forms.onboarding.firstName' }),
          }}
        />

        <TextField
          sx={{
            '.MuiFilledInput-input': { borderRadius: '4px 4px 0 0 !important' },
          }}
          error={
            (formik.touched.last_name && Boolean(formik.errors.last_name)) ||
            (choseToStay && keysWithChanges.includes('last_name'))
          }
          fullWidth
          helperText={
            formik.touched.last_name && formik.errors.last_name && intl.formatMessage({ id: formik.errors.last_name })
          }
          label={
            <>
              {intl.formatMessage({ id: 'forms.onboarding.lastName' })}
              <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
            </>
          }
          name="last_name"
          onBlur={formik.handleBlur}
          onChange={(e) => {
            formik.handleChange(e);
            handleDiffs('last_name', e);
          }}
          onKeyDown={preventSubmitOnEnter}
          type="text"
          value={formik.values.last_name}
          variant="filled"
          inputProps={{
            title: intl.formatMessage({ id: 'forms.onboarding.lastName' }),
          }}
        />
        {/*e-mail */}
        <Box sx={{ position: 'relative' }}>
          <TextField
            sx={{
              '.MuiFilledInput-input': { borderRadius: '4px 4px 0 0 !important' },
            }}
            error={
              (formik.touched.email && Boolean(formik.errors.email)) ||
              (choseToStay && keysWithChanges.includes('email'))
            }
            disabled
            fullWidth
            helperText={formik.touched.email && formik.errors.email && intl.formatMessage({ id: formik.errors.email })}
            label={
              <>
                {intl.formatMessage({ id: 'forms.signIn.username' })}
                <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
              </>
            }
            name="email"
            onBlur={formik.handleBlur}
            onChange={(e) => {
              formik.handleChange(e);
              handleDiffs('email', e);
            }}
            onKeyDown={preventSubmitOnEnter}
            type="text"
            value={formik.values.email}
            variant="filled"
            inputProps={{
              title: intl.formatMessage({ id: 'forms.signIn.username' }),
            }}
          />
          <InfoTooltip
            alt={intl.formatMessage(
              { id: 'profile.infoAlt' },
              { field: intl.formatMessage({ id: 'forms.signIn.username' }) }
            )}
            tooltip={
              <>
                <span style={{ fontSize: 14 }}>
                  {intl.formatMessage(
                    { id: 'profile.tooltips.email' },
                    {
                      link: (
                        <a
                          href="https://login.gov"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'white', display: 'inline-block' }}
                        >
                          <Typography
                            sx={{
                              color: 'white',
                              fontSize: 14,
                              display: 'inline-block',
                              fontWeight: 500,
                              textDecoration: 'underline',
                            }}
                          >
                            login.gov
                          </Typography>
                        </a>
                      ),
                    }
                  )}
                </span>
                <span style={{ fontSize: 14, display: 'block', paddingBottom: 5, paddingTop: 3 }}>
                  {intl.formatMessage({ id: 'profile.tooltips.email2' })}
                </span>
              </>
            }
            mobileContent={
              <>
                {intl.formatMessage(
                  { id: 'profile.tooltips.email' },
                  {
                    link: (
                      <a
                        href="https://login.gov"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'inline-block' }}
                      >
                        <Typography
                          sx={{
                            fontSize: 14,
                            display: 'inline-block',
                            fontWeight: 500,
                            textDecoration: 'underline',
                            color: 'primary.main',
                          }}
                        >
                          login.gov
                        </Typography>
                      </a>
                    ),
                  }
                )}
                <span style={{ display: 'block' }}>{intl.formatMessage({ id: 'profile.tooltips.email2' })}</span>
              </>
            }
          />
        </Box>
        {/* Education Level */}
        <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <FormControl variant="filled" error={!!formik.touched.degree && !!formik.errors.degree}>
            <InputLabel id="education-level-label" sx={{ top: 2 }} htmlFor="education-level">
              {intl.formatMessage({ id: 'forms.onboarding.educationLevel.titlecase' })}
              <span style={{ color: propOr('red', 'errorColorMain', cssVars) }}>*</span>
            </InputLabel>
            <Select
              name="degree"
              labelId="education-level-label"
              id="education-level"
              inputProps={{
                id: 'education-level',
              }}
              value={formik.values.degree}
              onChange={(e) => {
                formik.handleChange(e);
                handleDiffs('degree', e);
              }}
              error={
                (formik.touched.degree && Boolean(formik.errors.degree)) ||
                (choseToStay && keysWithChanges.includes('degree'))
              }
              onKeyDown={preventSubmitOnEnter}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  variant: 'outlined',
                },
                sx: {
                  maxHeight: 440,
                  '& .MuiPaper-root': {
                    borderRight: '1px solid #ccc', // Set the border style and color
                    borderBottom: '1px solid #ccc', // Set the border style and color
                    borderLeft: '1px solid #ccc', // Set the border style and color
                  },
                },
              }}
              sx={{
                '.MuiFilledInput-input': { borderRadius: '4px 4px 0 0 !important' },
              }}
            >
              {educationOptions.map((eo) => (
                <MenuItem key={eo.value} value={eo.value}>
                  {eo.title}
                </MenuItem>
              ))}
            </Select>
            {!!formik.touched.degree && !!formik.errors.degree && (
              <FormHelperText>
                {formik.errors.degree ? intl.formatMessage({ id: formik.errors.degree }) : ''}
              </FormHelperText>
            )}
          </FormControl>
          <InfoTooltip
            alt={intl.formatMessage(
              { id: 'profile.infoAlt' },
              { field: intl.formatMessage({ id: 'forms.onboarding.educationLevel.titlecase' }) }
            )}
            tooltip={<span style={{ fontSize: 14 }}>{intl.formatMessage({ id: 'profile.tooltips.education' })}</span>}
            mobileContent={intl.formatMessage({ id: 'profile.tooltips.education' })}
          />
        </Box>
        {/** Phone */}
        <Box sx={{ position: 'relative' }}>
          <TextField
            sx={{
              '.MuiFilledInput-input': { borderRadius: '4px 4px 0 0 !important' },
            }}
            error={
              (formik.touched.phone_number && Boolean(formik.errors.phone_number)) ||
              (choseToStay && keysWithChanges.includes('phone_number'))
            }
            name="phone_number"
            type="text"
            variant="filled"
            fullWidth
            helperText={
              formik.touched.phone_number &&
              formik.errors.phone_number &&
              intl.formatMessage({ id: formik.errors.phone_number }, { amount: 10 })
            }
            autoComplete="off"
            label={intl.formatMessage({ id: 'forms.onboarding.phoneNumber' })}
            value={formik.values.phone_number}
            onChange={(e) => {
              formik.handleChange(e);
              handleDiffs('phone_number', e);
            }}
            onKeyDown={preventSubmitOnEnter}
            onBlur={formik.handleBlur}
            InputProps={{
              inputComponent: PhoneNumberInput,
            }}
            inputProps={{
              title: intl.formatMessage({ id: 'forms.onboarding.phoneNumber' }),
            }}
          />
          <InfoTooltip
            alt={intl.formatMessage(
              { id: 'profile.infoAlt' },
              { field: intl.formatMessage({ id: 'forms.onboarding.phoneNumber' }) }
            )}
            tooltip={<span style={{ fontSize: 14 }}>{intl.formatMessage({ id: 'profile.tooltips.phone' })}</span>}
            mobileContent={intl.formatMessage({ id: 'profile.tooltips.phone' })}
          />
        </Box>
      </Box>
    </div>
  );
});

ContactForm.propTypes = {
  choseToStay: PropTypes.bool,
  classNames: PropTypes.string,
  valueChanged: PropTypes.func,
  initialValues: PropTypes.object,
};

export default Profile;
